import { useState, useMemo, useEffect } from 'react';
import SyncNode from '../components/SyncNode';
import { INSIGHT_STATUS, IS_MOCK_DEMO } from '../constants';
import { layoutRootTagNodes, getSyncHandlePair, getSyncNodeSize, filterTagsBySelectionAndQuery } from '../lib/graphUtils';

export function useSyncGraph({ notes, insights, concepts, normalizedTags = [], mapFilterMode, mapSearchQuery, isLargeScreen, globalSelectedTags = [] }) {
  const [syncHistory, setSyncHistory] = useState([{ id: 'root', label: 'Think' }]);
  const [selectedSyncNodeId, setSelectedSyncNodeId] = useState(null);

  // Reset navigation when filters change to show the root view of the new results
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    setSyncHistory([{ id: 'root', label: 'Think' }]);
    setSelectedSyncNodeId(null);
  }, [mapFilterMode, mapSearchQuery, globalSelectedTags]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const syncNodeTypes = useMemo(() => ({ syncNode: SyncNode }), []);

  const syncTags = useMemo(() => {
    // Calculate related tags count for importance scoring
    const relatedTagsCount = new Map();
    const addTagLinks = (items) => {
      items.forEach((item) => {
        const itemTags = Array.from(new Set((item.tags || []).filter(Boolean)));
        itemTags.forEach((tag) => {
          if (!relatedTagsCount.has(tag)) relatedTagsCount.set(tag, new Set());
          itemTags.forEach((otherTag) => {
            if (tag !== otherTag) relatedTagsCount.get(tag).add(otherTag);
          });
        });
      });
    };
    addTagLinks(notes);
    addTagLinks(insights);
    addTagLinks(concepts);

    const getImportanceScore = (tag) => {
      const relCount = relatedTagsCount.get(tag.name)?.size || 0;
      return relCount * 1 + (tag.assets || 0) * 3;
    };

    const isMock = typeof IS_MOCK_DEMO !== 'undefined' ? IS_MOCK_DEMO : true;
    if (isMock || normalizedTags.length === 0) {
      const tagSync = new Map();
      const addTags = (items, target) => {
        items.forEach((item) => {
          (item.tags || []).filter(Boolean).forEach((tag) => {
            const current = tagSync.get(tag) || { name: tag, notes: 0, insights: 0, assets: 0, unprocessedHints: 0, lastUpdatedAt: 0 };
            const isUnprocessedHint = target === 'insights' && item.status === INSIGHT_STATUS.SUGGESTED;
            const targetDate = item.updatedAt || item.createdAt;
            const itemUpdatedAt = targetDate ? (typeof targetDate.toMillis === 'function' ? targetDate.toMillis() : new Date(targetDate).getTime()) : 0;

            tagSync.set(tag, {
              ...current,
              [target]: current[target] + 1,
              unprocessedHints: current.unprocessedHints + (isUnprocessedHint ? 1 : 0),
              lastUpdatedAt: Math.max(current.lastUpdatedAt, itemUpdatedAt),
            });
          });
        });
      };

      addTags(notes, 'notes');
      addTags(insights, 'insights');
      addTags(concepts, 'assets');

      let filteredTags = Array.from(tagSync.values());

      filteredTags = filterTagsBySelectionAndQuery(filteredTags, globalSelectedTags, mapSearchQuery);

      if (mapFilterMode === 'recent') {
        filteredTags.sort((a, b) => b.lastUpdatedAt - a.lastUpdatedAt || a.name.localeCompare(b.name, 'ja'));
      } else if (mapFilterMode === 'unprocessed') {
        filteredTags.sort((a, b) => b.unprocessedHints - a.unprocessedHints || (b.notes + b.insights + b.assets) - (a.notes + a.insights + a.assets) || a.name.localeCompare(b.name, 'ja'));
        filteredTags = filteredTags.filter(tag => tag.unprocessedHints > 0);
      } else if (mapFilterMode === 'all') {
        filteredTags.sort((a, b) => (b.notes + b.insights + b.assets) - (a.notes + a.insights + a.assets) || a.name.localeCompare(b.name, 'ja'));
      } else {
        // important (default)
        filteredTags.sort((a, b) => {
          return getImportanceScore(b) - getImportanceScore(a) || a.name.localeCompare(b.name, 'ja');
        });
      }

      const limit = mapFilterMode === 'all' ? 60 : (isLargeScreen ? 16 : 10);
      return {
        items: filteredTags.slice(0, limit),
        totalCount: filteredTags.length,
      };
    }

    let filteredTags = normalizedTags.map(t => ({
      name: t.name,
      notes: t.noteCount || 0,
      insights: t.hintCount || 0,
      assets: t.assetCount || 0,
      unprocessedHints: 0, // Need to implement if we want this exact behavior
      lastUpdatedAt: t.lastUsedAt ? (typeof t.lastUsedAt.toMillis === 'function' ? t.lastUsedAt.toMillis() : new Date(t.lastUsedAt).getTime()) : 0,
    }));

    filteredTags = filterTagsBySelectionAndQuery(filteredTags, globalSelectedTags, mapSearchQuery);

    if (mapFilterMode === 'recent') {
      filteredTags.sort((a, b) => b.lastUpdatedAt - a.lastUpdatedAt || a.name.localeCompare(b.name, 'ja'));
    } else if (mapFilterMode === 'unprocessed') {
      // In normalized tags, we don't have unprocessedHints count yet, but we can approximate or ignore
      filteredTags.sort((a, b) => (b.notes + b.insights + b.assets) - (a.notes + a.insights + a.assets) || a.name.localeCompare(b.name, 'ja'));
    } else if (mapFilterMode === 'all') {
      filteredTags.sort((a, b) => (b.notes + b.insights + b.assets) - (a.notes + a.insights + a.assets) || a.name.localeCompare(b.name, 'ja'));
    } else {
      // important (default)
      filteredTags.sort((a, b) => {
        return getImportanceScore(b) - getImportanceScore(a) || a.name.localeCompare(b.name, 'ja');
      });
    }
    const limit = mapFilterMode === 'all' ? 60 : (isLargeScreen ? 16 : 10);
    return {
      items: filteredTags.slice(0, limit),
      totalCount: filteredTags.length,
    };
  }, [concepts, insights, notes, normalizedTags, mapFilterMode, mapSearchQuery, isLargeScreen, globalSelectedTags]);

  const syncGraph = useMemo(() => {
    const activeInsights = insights.filter(i => i.status !== INSIGHT_STATUS.ACCEPTED);
    const nodesById = new Map();
    const relations = {};
    const tagCooccurrence = new Map();
    const tagAdjacency = new Map(); // O(1) related tag lookup
    const allTagsMap = new Map();
    const putNode = (node) => nodesById.set(node.id, node);

    // Optimized: Consolidate tag stats and co-occurrence in a single O(N) pass
    const processItems = (items, target, weight = 1) => {
      items.forEach((item) => {
        const itemTags = Array.from(new Set((item.tags || []).filter(Boolean)));

        // 1. Update tag statistics
        itemTags.forEach(t => {
          const curr = allTagsMap.get(t) || { name: t, notes: 0, insights: 0, assets: 0, unprocessedHints: 0 };
          const isUnprocessedHint = target === 'insights' && item.status === INSIGHT_STATUS.SUGGESTED;
          allTagsMap.set(t, {
            ...curr,
            [target]: curr[target] + 1,
            unprocessedHints: curr.unprocessedHints + (isUnprocessedHint ? 1 : 0)
          });
        });

        // 2. Update co-occurrence and adjacency
        for (let i = 0; i < itemTags.length; i++) {
          const tagA = itemTags[i];
          for (let j = i + 1; j < itemTags.length; j++) {
            const tagB = itemTags[j];
            // Key order doesn't matter for adjacency, but we normalize for co-occurrence Map
            const [first, second] = tagA < tagB ? [tagA, tagB] : [tagB, tagA];
            const key = `${first}\u0000${second}`;
            tagCooccurrence.set(key, (tagCooccurrence.get(key) || 0) + weight);

            // Update adjacency map of maps for O(RelatedTags) lookup instead of O(TotalTags)
            if (!tagAdjacency.has(tagA)) tagAdjacency.set(tagA, new Map());
            if (!tagAdjacency.has(tagB)) tagAdjacency.set(tagB, new Map());
            tagAdjacency.get(tagA).set(tagB, (tagAdjacency.get(tagA).get(tagB) || 0) + weight);
            tagAdjacency.get(tagB).set(tagA, (tagAdjacency.get(tagB).get(tagA) || 0) + weight);
          }
        }
      });
    };

    processItems(notes, 'notes', 1);
    processItems(activeInsights, 'insights', 1);
    processItems(concepts, 'assets', 2);

    // Initial root relation from syncTags
    relations.root = syncTags.items.map((tag) => `tag:${tag.name}`);

    putNode({
      id: 'root',
      type: 'root',
      label: 'Think',
      description: 'タグを起点に、Asset / Hint、出典Note、Rawログへ潜るThinkです。',
    });

    // For each tag that exists in our system, define its relations and ensure it's a node
    allTagsMap.forEach((tagStats, tagName) => {
      const tagId = `tag:${tagName}`;
      
      // Find top related tags - Optimized using tagAdjacency
      const relatedTags = tagAdjacency.get(tagName);
      const relatedTagWeights = [];
      if (relatedTags) {
        relatedTags.forEach((count, otherTagName) => {
          relatedTagWeights.push({ id: `tag:${otherTagName}`, count });
        });
      }
      relatedTagWeights.sort((a, b) => b.count - a.count);

      // Basic node info
      putNode({
        id: tagId,
        type: 'tag',
        label: `#${tagName}`,
        tagName: tagName,
        weight: tagStats.notes + tagStats.insights + tagStats.assets,
        description: `${tagStats.notes} Notes / ${tagStats.insights} Hints / ${tagStats.assets} Assets`,
        assetCount: tagStats.assets,
        unprocessedHintCount: tagStats.unprocessedHints,
        relatedTagCount: relatedTagWeights.length,
      });

      const tagAssets = concepts.filter((asset) => (asset.tags || []).includes(tagName));
      const tagInsights = activeInsights.filter((insight) => (insight.tags || []).includes(tagName));
      const tagNotes = notes.filter((note) => (note.tags || []).includes(tagName));

      const tagChildren = [
        ...tagAssets.slice(0, 4).map((asset) => `asset:${asset.id}`),
        ...tagInsights.slice(0, 4).map((insight) => `insight:${insight.id}`),
      ];

      // Fallback: show notes only if there are no assets
      if (tagAssets.length === 0) {
        tagChildren.push(...tagNotes.slice(0, 4).map((note) => `note:${note.id}`));
      }

      relations[tagId] = tagChildren;
    });

    concepts.forEach((asset) => {
      const nodeId = `asset:${asset.id}`;
      const noteIds = new Set(asset.sourceNoteIds || []);
      const sourceInsightIds = new Set(asset.sourceInsightIds || []);
      activeInsights
        .filter((insight) => sourceInsightIds.has(insight.id))
        .forEach((insight) => (insight.sourceNoteIds || []).forEach((noteId) => noteIds.add(noteId)));

      putNode({
        id: nodeId,
        type: 'asset',
        entityId: asset.id,
        label: asset.title || 'Untitled Asset',
        description: asset.body || '',
        status: asset.status,
      });
      relations[nodeId] = Array.from(noteIds).slice(0, 6).map((noteId) => `note:${noteId}`);
    });

    activeInsights.forEach((insight) => {
      const nodeId = `insight:${insight.id}`;
      putNode({
        id: nodeId,
        type: 'insight',
        entityId: insight.id,
        label: insight.title || 'Untitled Hint',
        description: insight.summary || '',
        parseFailed: insight.metadata?.parseFailed || false,
        status: insight.status,
      });
      relations[nodeId] = (insight.sourceNoteIds || []).slice(0, 6).map((noteId) => `note:${noteId}`);
    });

    notes.forEach((note) => {
      const nodeId = `note:${note.id}`;
      putNode({
        id: nodeId,
        type: 'note',
        entityId: note.id,
        label: note.title || 'Untitled Note',
        description: note.summary || note.body || '',
      });
      relations[nodeId] = note.sourceMessageIds?.length ? [`raw:${note.id}`] : [];
      putNode({
        id: `raw:${note.id}`,
        type: 'raw',
        entityId: note.id,
        label: `${note.sourceMessageIds?.length || 0} raw`,
        description: '元のチャットログをSinkで確認できます。',
      });
      relations[`raw:${note.id}`] = [];
    });

    const maxTagCooccurrence = Math.max(...tagCooccurrence.values(), 0);
    const visibleTagNames = new Set(syncTags.items.map(t => t.name));
    const tagLinks = Array.from(tagCooccurrence.entries()).map(([key, count]) => {
      const [fromTag, toTag] = key.split('\u0000');
      return {
        from: `tag:${fromTag}`,
        to: `tag:${toTag}`,
        count,
        strength: maxTagCooccurrence > 0 ? count / maxTagCooccurrence : 0,
        fromName: fromTag,
        toName: toTag,
      };
    }).filter(link => visibleTagNames.has(link.fromName) && visibleTagNames.has(link.toName));

    // Pre-calculate childCount and importance score for sorting and display
    nodesById.forEach((node) => {
      // Calculate childCount (batch count) for display on nodes
      if (node.type === 'tag') {
        node.childCount = (node.assetCount || 0) + (node.unprocessedHintCount || 0);
        // Also calculate importance score for sorting
        const tagStats = allTagsMap.get(node.tagName);
        const relCount = node.relatedTagCount || 0;
        node.importanceScore = relCount * 1 + (tagStats?.assets || 0) * 3;
      } else {
        node.childCount = (relations[node.id] || []).filter(childId => {
          const childNode = nodesById.get(childId);
          if (!childNode) return false;
          return childNode.type === 'asset' || (childNode.type === 'insight' && childNode.status === INSIGHT_STATUS.SUGGESTED);
        }).length;
        // For non-tag nodes, use childCount or other metrics for score if needed, 
        // but primarily we sort tags at root by importanceScore.
        node.importanceScore = node.childCount; 
      }
    });

    return { nodesById, relations, tagLinks };
  }, [concepts, insights, syncTags, notes]);

  const syncCurrentId = syncHistory[syncHistory.length - 1]?.id || 'root';
  const syncCurrentNode = syncGraph.nodesById.get(syncCurrentId) || syncGraph.nodesById.get('root');
  const explicitSelectedNode = selectedSyncNodeId ? syncGraph.nodesById.get(selectedSyncNodeId) : null;
  const selectedSyncNode = explicitSelectedNode || (syncCurrentId === 'root' ? null : syncCurrentNode);

  const syncDisplayNodes = useMemo(() => {
    const children = syncGraph.relations[syncCurrentId] || [];
    if (syncCurrentId === 'root') {
      return layoutRootTagNodes(children, syncGraph.nodesById);
    }

    const radiusX = 290;
    const radiusY = 210;
    const center = syncGraph.nodesById.get(syncCurrentId);
    const visibleNodes = center ? [{ ...center, x: 0, y: 0, scale: 1.15, isCenter: true }] : [];

    const isTagView = syncCurrentNode?.type === 'tag';

    if (isTagView) {
      const typePriority = { tag: 1, asset: 2, insight: 3, note: 4, raw: 5 };
      const sortedChildren = children
        .map((childId) => syncGraph.nodesById.get(childId))
        .filter(Boolean)
        .sort((a, b) => {
          // Primary: Type priority
          const typeDiff = (typePriority[a.type] || 99) - (typePriority[b.type] || 99);
          if (typeDiff !== 0) return typeDiff;
          // Secondary: importanceScore descending (内側から降順)
          return (b.importanceScore || 0) - (a.importanceScore || 0) || a.label.localeCompare(b.label, 'ja');
        });

      const total = sortedChildren.length;
      const startAngle = -Math.PI / 2; // 12 o'clock

      sortedChildren.forEach((node, index) => {
        // Evenly space around the full circle
        const angle = startAngle + ((index + 0.5) / Math.max(total, 1)) * Math.PI * 2;
        // radiusOffset: index % 2 === 0 is -25 (inside), index % 2 === 1 is 25 (outside)
        const radiusOffset = total > 3 ? (index % 2 === 0 ? -25 : 25) : 0;
        
        visibleNodes.push({
          ...node,
          x: Math.cos(angle) * (radiusX + radiusOffset),
          y: Math.sin(angle) * (radiusY + radiusOffset),
          scale: 1,
          isCenter: false,
        });
      });
    } else {
      const sortedChildren = children
        .map((childId) => syncGraph.nodesById.get(childId))
        .filter(Boolean)
        .sort((a, b) => (b.importanceScore || 0) - (a.importanceScore || 0) || a.label.localeCompare(b.label, 'ja'));

      sortedChildren.forEach((node, index) => {
        const angle = (index / Math.max(sortedChildren.length, 1)) * Math.PI * 2 - Math.PI / 2;
        visibleNodes.push({
          ...node,
          x: Math.cos(angle) * radiusX,
          y: Math.sin(angle) * radiusY,
          scale: 1,
          isCenter: false,
        });
      });
    }

    return visibleNodes;
  }, [syncCurrentId, syncGraph, syncCurrentNode]);

  const syncLinks = useMemo(() => (
    syncCurrentId === 'root'
      ? syncGraph.tagLinks
      : (syncGraph.relations[syncCurrentId] || []).map((childId) => ({ from: syncCurrentId, to: childId }))
  ), [syncCurrentId, syncGraph]);

  const syncFlowNodes = useMemo(() => (
    syncDisplayNodes.map((node) => {
      const size = getSyncNodeSize(node.type, node.weight, node.label);
      const childCount = node.childCount || 0;

      return {
        id: node.id,
        type: 'syncNode',
        data: {
          label: node.label,
          type: node.type,
          childCount,
          weight: node.weight,
          parseFailed: node.parseFailed,
          status: node.status,
        },
        position: {
          x: node.x - size.width / 2,
          y: node.y - size.height / 2,
        },
        width: size.width,
        height: size.height,
        style: {
          background: 'transparent',
          border: 0,
          padding: 0,
        },
        draggable: true,
        selected: selectedSyncNode?.id === node.id,
      };
    })
  ), [syncDisplayNodes, selectedSyncNode]);


  const syncFlowEdges = useMemo(() => (
    syncLinks.map((link) => {
      const sourceNode = syncDisplayNodes.find((node) => node.id === link.from);
      const targetNode = syncDisplayNodes.find((node) => node.id === link.to);
      const handles = sourceNode && targetNode ? getSyncHandlePair(sourceNode, targetNode) : {};
      return {
        id: `${link.from}-${link.to}`,
        source: link.from,
        target: link.to,
        ...handles,
        animated: syncCurrentId !== 'root',
        type: 'default',
        style: {
          stroke: syncCurrentId === 'root' ? '#818cf8' : '#a5b4fc',
          strokeOpacity: link.strength ? 0.18 + link.strength * 0.62 : 0.5,
          strokeWidth: link.strength ? 1 + link.strength * 2.2 : 1.5,
        },
      };
    })
  ), [syncCurrentId, syncDisplayNodes, syncLinks]);

  const handleSyncNodeClick = (node) => {
    if (node.id === syncCurrentId) {
      if (syncHistory.length > 1) {
        setSyncHistory((current) => current.slice(0, -1));
        setSelectedSyncNodeId(null);
      }
      return;
    }
    setSelectedSyncNodeId(node.id);
  };

  const handleDrillDown = (node) => {
    if (!node) return;
    const childIds = syncGraph.relations[node.id] || [];
    if (childIds.length > 0 || node.type !== 'raw') {
      setSyncHistory((current) => [...current, { id: node.id, label: node.label }]);
      setSelectedSyncNodeId(null);
    }
  };

  const navigateSyncHistory = (index) => {
    setSyncHistory((current) => current.slice(0, index + 1));
    setSelectedSyncNodeId(null);
  };

  const handleJumpToMap = (nodeId, label) => {
    if (!nodeId) return;
    setSyncHistory([{ id: 'root', label: 'Think' }, { id: nodeId, label: label || 'Jump' }]);
    setSelectedSyncNodeId(null);
  };

  return {
    syncHistory,
    setSyncHistory,
    selectedSyncNodeId,
    setSelectedSyncNodeId,
    syncNodeTypes,
    syncTags,
    syncTagsCount: syncTags.totalCount,
    syncGraph,
    syncCurrentId,
    syncCurrentNode,
    selectedSyncNode,
    syncDisplayNodes,
    syncFlowNodes,
    syncFlowEdges,
    handleSyncNodeClick,
    handleDrillDown,
    navigateSyncHistory,
    handleJumpToMap,
  };
}
